import React from 'react';
import type { ErrorInfo, PropsWithChildren } from 'react';
import { Button, Result } from 'antd';
import type { ModalFuncProps } from 'antd';
import { Link } from 'react-router-dom';
import { useCustomMessage } from '../../../hooks';
import { ContextApp } from '../../../contexts/ContextApp';

interface ExceptionManagerState {
  readonly hasError: Error | null;
}

interface ExceptionManagerContentProps extends PropsWithChildren {
  readonly messageModal?: string;
  readonly messageComponent?: React.JSX.Element;
  readonly showWarning: (op: {
    context: string;
    message: string;
    props?: ModalFuncProps;
  }) => void;
  readonly translate: (key: string) => string;
}

export interface ExceptionManagerProps extends PropsWithChildren {
  readonly messageModal?: string;
  readonly messageComponent?: React.JSX.Element;
}

class ExceptionManagerContent extends React.Component<
  ExceptionManagerContentProps,
  ExceptionManagerState
> {
  state: ExceptionManagerState = { hasError: null };

  static getDerivedStateFromError(error: Error): ExceptionManagerState {
    return { hasError: error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { messageModal, showWarning, translate } = this.props;
    console.error(error);

    if (messageModal) {
      showWarning({
        context: 'ExceptionManager.componentDidCatch',
        message: messageModal,
        props: {
          title: translate('abm.oops'),
          content: `${messageModal}. ${translate('message.reportAdmin')}`,
        },
      });
    }

    console.error(errorInfo.componentStack);
  }

  render() {
    const message = this.props.messageComponent ?? (
      <Result
        status="warning"
        title={this.props.translate('error.unexpectedPageError')}
        extra={
          <Button type="primary">
            <Link to="/">{this.props.translate('action.continue')}</Link>
          </Button>
        }
      />
    );

    if (this.props.messageModal) {
      return this.state.hasError ? null : this.props.children;
    }
    return this.state.hasError ? message : this.props.children;
  }
}

const ExceptionManager = (props: Readonly<ExceptionManagerProps>) => {
  const { messageModalWarning } = useCustomMessage();
  const { t } = React.useContext(ContextApp);
  const translate = (key: string): string =>
    t(key as Parameters<typeof t>[0], { returnObjects: false }) as string;

  return (
    <ExceptionManagerContent
      messageModal={props.messageModal}
      messageComponent={props.messageComponent}
      showWarning={messageModalWarning}
      translate={translate}
    >
      {props.children}
    </ExceptionManagerContent>
  );
};

export default ExceptionManager;
